import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { invalidateWorkerRequirements } from "@src/appV2/redesign/Documents/api/invalidateWorkerRequirements";
import { useDocumentUploadFlow } from "@src/appV2/redesign/Documents/Details/Upload/useDocumentUploadFlow";
import { useRequirementDetailsContext } from "@src/appV2/redesign/Documents/Details/useRequirementDetailsContext";
import { DOCUMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { useFileUploaderContext } from "@src/appV2/redesign/Documents/useFileUploaderContext";
import { useQueryClient } from "@tanstack/react-query";
import { isBefore, startOfDay } from "date-fns";
import { useCallback, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { getMinimumTestDate, TB_TEST_GUIDED_UPLOAD_ANALYTICS_FLOW } from "../constants";
import { type TbTestGuidedUploadStep, type TbTestType } from "../types";

export function useTbTestGuidedUploadWorkflow() {
  const { requirement, setUploadedDocument } = useRequirementDetailsContext();
  const { uploadedFiles, clearFiles } = useFileUploaderContext();
  const { mutateAsync: uploadDocument, isLoading: isUploading } = useDocumentUploadFlow();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const history = useHistory();

  const { id: requirementId = "", attributes } = requirement.requirement ?? {};
  const { name: requirementName = "" } = attributes ?? {};

  const [step, setStep] = useState<TbTestGuidedUploadStep>("test-type-selection");
  const [testType, setTestType] = useState<TbTestType | undefined>(undefined);
  const [selectedTbTestDate, setSelectedTbTestDate] = useState<Date | undefined>(undefined);

  const reset = useCallback(() => {
    setStep("test-type-selection");
    setTestType(undefined);
    setSelectedTbTestDate(undefined);
    clearFiles();
  }, [clearFiles]);

  const navigateToDetails = useCallback(() => {
    clearFiles();
    history.replace(generatePath(DOCUMENT_DETAILS_FULL_PATH, { requirementId }));
  }, [clearFiles, history, requirementId]);

  const handleSelectTestType = useCallback((type: TbTestType) => {
    setTestType(type);
  }, []);

  const handleNext = useCallback(() => {
    switch (step) {
      case "test-type-selection": {
        if (!isDefined(testType)) {
          return;
        }

        logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_TEST_TYPE_SELECTED, {
          requirementId,
          testType,
        });

        setStep("instructions");
        break;
      }
      case "instructions": {
        setStep("upload");
        break;
      }
      case "upload": {
        setStep("date-validation");
        break;
      }
      case "date-validation": {
        // Handled by handleTbTestDateSubmitted
        break;
      }
      case "invalid-test-error": {
        reset();
        break;
      }
      case "success": {
        logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_COMPLETED, {
          requirementId,
          testType,
        });
        navigateToDetails();
        break;
      }
      default:
        step satisfies never; // Exhaustive check to ensure all steps are handled
        break;
    }
  }, [step, testType, requirementId, navigateToDetails, reset]);

  const handleBack = useCallback(() => {
    switch (step) {
      case "test-type-selection": {
        logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_ABANDONED, {
          requirementId,
          abandonedAtStep: step,
          testType,
        });
        navigateToDetails();
        break;
      }
      case "instructions": {
        setStep("test-type-selection");
        break;
      }
      case "upload": {
        setStep("instructions");
        break;
      }
      case "date-validation": {
        setStep("upload");
        break;
      }
      case "invalid-test-error": {
        setSelectedTbTestDate(undefined);
        setStep("date-validation");
        break;
      }
      case "success": {
        navigateToDetails();
        break;
      }
      default:
        step satisfies never;
        break;
    }
  }, [step, testType, requirementId, navigateToDetails]);

  const handleDateChange = useCallback((date: Date) => {
    setSelectedTbTestDate(date);
  }, []);

  const handleTbTestDateSubmitted = useCallback(async () => {
    // These should never be undefined, but we'll check just to be safe
    if (!isDefined(selectedTbTestDate) || !isDefined(testType)) {
      return;
    }

    const minimumTestDate = startOfDay(getMinimumTestDate(testType));
    if (isBefore(startOfDay(selectedTbTestDate), minimumTestDate)) {
      logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_INVALID_TEST_DATE, {
        requirementId,
        testType,
        selectedDate: selectedTbTestDate.toISOString(),
      });
      setStep("invalid-test-error");
      return;
    }

    try {
      const { data } = await uploadDocument({
        requirementName,
        requirementId,
        files: uploadedFiles,
      });

      setUploadedDocument(data);
      void invalidateWorkerRequirements(queryClient);
      clearFiles();

      logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_DOCUMENT_UPLOADED, {
        requirementId,
        testType,
      });

      // Emit standard upload event to match normal document upload flow (see PreviewPage.tsx)
      logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_SUCCESS, {
        requirement_id: requirementId,
        flow: TB_TEST_GUIDED_UPLOAD_ANALYTICS_FLOW,
      });

      setStep("success");
    } catch (error) {
      logError(APP_V2_APP_EVENTS.TB_TEST_GUIDED_UPLOAD_UPLOAD_FAILED, {
        error,
        metadata: {
          requirementId,
          testType,
        },
      });

      // Emit standard upload event to match normal document upload flow (see PreviewPage.tsx)
      logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_FAILURE, {
        requirement_id: requirementId,
        flow: TB_TEST_GUIDED_UPLOAD_ANALYTICS_FLOW,
      });
      showErrorToast("Failed to upload document. Please try again.");
    }
  }, [
    selectedTbTestDate,
    requirementId,
    requirementName,
    testType,
    uploadedFiles,
    uploadDocument,
    setUploadedDocument,
    queryClient,
    clearFiles,
    showErrorToast,
  ]);

  const handleClose = useCallback(() => {
    if (step !== "success") {
      logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_ABANDONED, {
        requirementId,
        abandonedAtStep: step,
        testType,
      });
    }

    navigateToDetails();
  }, [step, testType, requirementId, navigateToDetails]);

  const hasFiles = uploadedFiles.length > 0;

  return {
    step,
    testType,
    selectedTbTestDate,
    isUploading,
    hasFiles,
    handleSelectTestType,
    handleNext,
    handleBack,
    handleClose,
    handleDateChange,
    handleTbTestDateSubmitted,
    reset,
  };
}
